import React = require("react");
import {Legend, LegendLiElement} from "../../../components/charts/Legend";
import {ColorUtils} from "../../../components/Utils/color/ColorUtils";
import {Loader} from "../../../components/Utils/loader/Loader";
import {StatisticsUtils} from "../../../components/Utils/statistics/StatisticsUtils";
import {AppTranslations} from "../../../components/Utils/translations/AppTranslations";
import {CategoriesColors, EquipmentTypeTexts, WidgetResponse} from "../../DashboardContainer";
import {JSONMapper} from "../../data/JSONMapper";
import {PieChartWithLabels} from "../charts/PieChartWithLabels";
import {LegendTooltip, LegendTooltipConfig} from "./LegendTooltip";
import {RestDataLoader} from "./RestDataLoader";
import "./style/EquipmentByModelWidget.scss";
import "./style/Legend.scss";
import {VeBoxCard} from "../../../components/card/VeBoxCard";
import {WidgetBadge, WidgetBadgeType} from "../DashboardWidget";
import AutoSizer from "react-virtualized-auto-sizer";

declare var AppTranslations: AppTranslations;

interface EquipmentByModelWidgetProps {
    data: WidgetResponse;
    texts: EquipmentTypeTexts;
    withTelemetryDevice: boolean;
    colorGenerator: ColorUtils;
}

interface WidgetState {
    ready: boolean;
    error: boolean;
    equipmentByModel: EquipmentByModel[];
    selectedItemName: string;
    tooltipConfig: LegendTooltipConfig;
}

export interface EquipmentByModel {
    modelGuid: string;
    modelId?: string;
    brandGuid?: string;
    modelName: string;
    typeGuid: string;
    typeColor?: string;
    equipmentCount: number;
}

export class EquipmentByModelWidget extends React.Component<EquipmentByModelWidgetProps, WidgetState> {

    constructor(props: EquipmentByModelWidgetProps) {
        super(props);
        this.state = {
            ready: false,
            error: false,
            equipmentByModel: null,
            selectedItemName: null,
            tooltipConfig: {visible: false},
        };
        this.setSelectedItem = this.setSelectedItem.bind(this);
    }

    public setSelectedItem(itemName: string, e): void {
        const target: HTMLLIElement = e ? e.currentTarget : null;
        this.setState({selectedItemName: itemName}, () => {
            if (itemName !== null && itemName.indexOf(this.props.texts.others) > -1) {
                const equipmentByModels = this.state.equipmentByModel
                    .sort((a, b) => b.equipmentCount - a.equipmentCount)
                    .filter((x, i) => i >= 11);

                if (target) {
                    this.setState({
                        tooltipConfig: {
                            visible: true,
                            offSetX: target.offsetLeft,
                            offSetY: target.offsetTop + target.offsetHeight,
                            width: target.offsetWidth,
                            data: equipmentByModels,
                            father: target,
                        },
                    });
                }
            }
        });
    }

    public ready() {
        if (this.state.equipmentByModel != null) {
            this.setState({ready: true});
        }
    }

    public componentDidMount() {
        RestDataLoader.loadData(
            "statistics/equipment/counting/model",
            {
                placements: "manufacturer,warehouse,market",
                withTelemetry: this.props.withTelemetryDevice,
                groupByBrand: true,
            },
            (data: any) => {
                this.setState({equipmentByModel: JSONMapper.jsonEquipmentByModelMapper(data.list)});
                this.ready();
            },
            () => {
                this.setState({error: true});
            },
        );
    }

    public render() {
        if (this.state.error) {
            return null;
        }
        let content = <Loader/>;
        if (!this.state.ready) {
            return (
                <div className="equipment-by-model-widget">
                    {content}
                </div>
            );
        }
        // data for label with icon
        const data = this.state.equipmentByModel
            .filter((e) => e.equipmentCount != null);

        const dataNotInOthersType = data
            .sort((a, b) => b.equipmentCount - a.equipmentCount)
            .filter((x, i) => i < 11);

        const dataGroupedByType = {};
        data.forEach((m) => {
            if (dataGroupedByType[m.typeGuid] === undefined) {
                dataGroupedByType[m.typeGuid] = [];
            }
            dataGroupedByType[m.typeGuid].push(m);
        });

        // order first models of the component types more present
        let totalsByTypes = [];
        Object.keys(dataGroupedByType)
            .forEach((t, i) => {
                totalsByTypes.push(
                    {
                        typeGuid: t,
                        tot: dataGroupedByType[t].map((e) => e.equipmentCount).reduce((a, b) => a + b, 0),
                    });
            });
        totalsByTypes = totalsByTypes.sort((a, b) => b.tot - a.tot);

        // for each component type define the color shade for its component models and the visualization order
        const dataSet = [];
        let order = 0;
        totalsByTypes.forEach((k, i) => {
            const result = dataGroupedByType[k.typeGuid].sort((a, b) => {
                return b.equipmentCount - a.equipmentCount;
            })
                .forEach((m, j) => {
                    const notInOther = dataNotInOthersType.filter((d) => d.typeGuid === m.typeGuid);
                    const colorShade = (notInOther.filter((o) => o.modelGuid === m.modelGuid).length === 0)
                        ? null
                        : (m.typeColor != null ?
                                this.props.colorGenerator.getColorShade(
                                    m.typeColor,
                                    j + 1,
                                    notInOther.length) :
                                this.props.colorGenerator.getMappedColorShade(
                                    CategoriesColors.COMPONENT_CATEGORIES,
                                    m.typeGuid,
                                    j + 1,
                                    notInOther.length)
                        );
                    dataSet.push({
                        guid: m.modelGuid,
                        label: m.modelName,
                        frequency: m.equipmentCount,
                        icon: null,
                        color: colorShade,
                        order,
                    });
                    order++;
                });
            return result;
        });

        // group exceeding component models into a single item called 'Others'
        const preparedData = PieChartWithLabels.prepareData(
            dataSet,
            12,
            "custom",
            {others: this.props.texts.others},
            undefined,
        );
        const total = dataSet.reduce<number>((a, x) => a + x.frequency, 0);
        const legendItems = preparedData.map((pd) => {
            return {
                color: pd.color,
                name: pd.label,
                value: pd.frequency,
                icon: pd.icon,
                percentage: StatisticsUtils.getPercentage(total, pd.frequency),
            };
        });

        const onOtherTooltipClicked = (equipmentModel) => () => this.onItemClick(equipmentModel);
        const onOtherTooltipHover = (_, e) => null;
        const pieChartText = {othersItem: this.props.texts.others};
        content = (
            <VeBoxCard className="equipment-by-model-widget__container home-dashboard-card">
                <VeBoxCard.Header
                    className="equipment-by-model-widget__header commercial-header space-between"
                    title={this.props.texts.title}
                    titleClassName={"lowercase capitalize"}
                    singleLineTitle={true}
                    back={
                        <WidgetBadge type={WidgetBadgeType.COMMERCIAL}/>
                    }
                />
                <VeBoxCard.Body className="equipment-by-model-widget__content">
                    <div className="pie-chart__legend__container">
                        <Legend
                            items={legendItems}
                            selectedItemName={this.state.selectedItemName}
                            onItemHover={this.setSelectedItem}
                            onClick={this.onItemClick}
                            showValues={true}
                        />
                        <LegendTooltip
                            config={this.state.tooltipConfig}
                            className={"tooltip-wrapper"}
                            style={{
                                color: "white",
                            }}
                        >
                            {(equipments: EquipmentByModel[]) => (
                                <div
                                    className={"tooltip-body"}
                                    style={{
                                        maxHeight: 200,
                                        overflow: "auto",
                                    }}
                                >
                                    <ul className="legend">
                                        {equipments.map((equipment, i) => (
                                            <LegendLiElement
                                                key={i}
                                                item={{
                                                    name: equipment.modelName,
                                                    color: legendItems[legendItems.length - 1].color,
                                                    code: equipment.typeGuid,
                                                    value: equipment.equipmentCount,
                                                    percentage: StatisticsUtils.getPercentage(
                                                        total,
                                                        equipment.equipmentCount,
                                                    ),
                                                }}
                                                iconOptions={{show: false}}
                                                onClick={onOtherTooltipClicked(equipment.modelName)}
                                                showValues={true}
                                                liStyle={{
                                                    color: "white",
                                                }}
                                                onHover={onOtherTooltipHover}
                                            />
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </LegendTooltip>
                    </div>
                    <div className="pie-chart__graph">
                        <AutoSizer style={{width: "auto"}}>
                            {({width, height}) => <PieChartWithLabels
                                donutOptions={{activeDonut: false}}
                                width={width - 40}
                                height={height * .8}
                                radiusRatio={2}
                                data={preparedData}
                                showPercentage={true}
                                showIcon={false}
                                noDataMessage={this.props.texts.noData}
                                labelLineDefaultLength={20}
                                order={"custom"}
                                groupingThreshold={15}
                                texts={pieChartText}
                                selectedItem={this.state.selectedItemName}
                                labelsOptions={{externalLegend: true}}
                                onPathOver={this.setSelectedItem}
                                onClick={this.onSliceClick}
                            />}
                        </AutoSizer>
                    </div>
                </VeBoxCard.Body>
            </VeBoxCard>
        );
        return (
            <div className="equipment-by-model-widget">
                {content}
            </div>
        );
    }

    private onItemClick = (key): void => {
        if (key.indexOf(this.props.texts.others) === -1) {
            const model = this.state.equipmentByModel.find((x) => x.modelName === key);
            (window as any).GlobalStackManager.addNewLevel("EquipmentListLevel", {
                equipmentModel: key,
                allTelemetries: this.props.withTelemetryDevice,
                modelWithBrandList: model.modelId ? [{modelId: model.modelId, brandGuid: model.brandGuid}] : null,
                applyFilter: true,
            });
        }
    }

    private onSliceClick = (d): void => {
        if (d.data.label.indexOf(this.props.texts.others) === -1) {
            const model = this.state.equipmentByModel.find((x) => x.modelName === d.data.label);
            (window as any).GlobalStackManager.addNewLevel("EquipmentListLevel", {
                equipmentModel: d.data.label,
                allTelemetries: this.props.withTelemetryDevice,
                modelWithBrandList: model.modelId ? [{modelId: model.modelId, brandGuid: model.brandGuid}] : null,
                applyFilter: true,
            });
        }
    }
}
